<template>
  <div class="widget-carousel-general-settings card border-0 shadow-sm compact-form-card">
    <div class="card-header bg-white border-bottom compact-form-card__header">
      <strong>Setări generale</strong>
    </div>

    <div class="card-body compact-form-card__body">
      <div class="widget-carousel-general-settings__section">
        <h5 class="widget-carousel-general-settings__section-title">
          Status & identitate
        </h5>

        <div class="form-row compact-form-row compact-form-row--top">
          <div class="form-group col-12 col-md-4 compact-form-group">
            <label class="compact-label">Status</label>

            <div class="widget-inline-field widget-inline-field--checkbox">
              <div class="custom-control custom-switch mb-0">
                <input
                    id="widget-carousel-is-active"
                    type="checkbox"
                    class="custom-control-input"
                    :checked="form.is_active"
                    @change="updateField('is_active', $event.target.checked)"
                >

                <label
                    class="custom-control-label compact-check-label"
                    for="widget-carousel-is-active"
                >
                  Widget activ
                </label>
              </div>
            </div>
          </div>

          <div class="form-group col-12 col-md-4 compact-form-group">
            <label for="widget-carousel-priority" class="compact-label">
              Priority
            </label>

            <div class="widget-inline-field">
              <select
                  id="widget-carousel-priority"
                  class="form-control compact-input"
                  :value="form.priority"
                  @change="updateField({ field: 'priority', value: Number($event.target.value) })"
              >
                <option
                    v-for="value in priorityOptions"
                    :key="value"
                    :value="value"
                >
                  {{ value }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-group col-12 col-md-4 compact-form-group">
            <label for="widget-carousel-type" class="compact-label">
              Tip produse
            </label>

            <div class="widget-inline-field">
              <select
                  id="widget-carousel-type"
                  class="form-control compact-input"
                  :value="form.type"
                  :disabled="hasProducts"
                  @change="updateField('type', $event.target.value)"
              >
                <option value="benefits">Benefits</option>
                <option value="offers">Offers</option>
              </select>
            </div>

            <small class="compact-help-text">
              Nu poți combina benefits cu offers în același widget.
            </small>
          </div>
        </div>

        <div class="form-row compact-form-row">
          <div class="form-group col-12 col-md-7 compact-form-group">
            <label for="widget-carousel-widget-id" class="compact-label">
              Widget ID
            </label>

            <input
                id="widget-carousel-widget-id"
                type="text"
                class="form-control compact-input"
                :value="form.widget_id"
                @input="onWidgetIdInput"
                placeholder="ex: carrefour_100_ron"
            >

            <small class="compact-help-text">
              Câmp obligatoriu. Se transformă automat într-un slug folosind underscore.
            </small>
          </div>

          <div class="form-group col-12 col-md-5 compact-form-group">
            <label for="widget-carousel-tracking" class="compact-label">
              Tracking
            </label>

            <input
                id="widget-carousel-tracking"
                type="text"
                class="form-control compact-input"
                :value="form.tracking"
                @input="updateField('tracking', $event.target.value)"
                placeholder="tracking"
            >
          </div>
        </div>
      </div>

      <div class="widget-carousel-general-settings__section">
        <h5 class="widget-carousel-general-settings__section-title">
          Disponibilitate
        </h5>

        <div class="form-row compact-form-row">
          <div class="form-group col-12 col-md-6 compact-form-group">
            <label for="widget-carousel-available-start-date" class="compact-label">
              Available start date & time
            </label>

            <input
                id="widget-carousel-available-start-date"
                type="datetime-local"
                class="form-control compact-input"
                :disabled="!form.is_active"
                :value="normalizeDateTimeLocal(form.available_start_date)"
                @input="updateField('available_start_date', $event.target.value)"
            >
          </div>

          <div class="form-group col-12 col-md-6 compact-form-group">
            <label for="widget-carousel-available-end-date" class="compact-label">
              Available end date & time
            </label>

            <input
                id="widget-carousel-available-end-date"
                type="datetime-local"
                class="form-control compact-input"
                :disabled="!form.is_active"
                :value="normalizeDateTimeLocal(form.available_end_date)"
                @input="updateField('available_end_date', $event.target.value)"
            >
          </div>
        </div>
      </div>

      <div class="widget-carousel-general-settings__section mb-0">
        <h5 class="widget-carousel-general-settings__section-title">
          Comportament widget
        </h5>

        <div class="form-row compact-form-row">
          <div class="form-group col-12 col-md-4 compact-form-group">
            <label for="widget-carousel-ratio" class="compact-label">
              Raport card
            </label>

            <input
                id="widget-carousel-ratio"
                type="number"
                class="form-control compact-input"
                min="2"
                max="10"
                :value="form.ratio"
                :disabled="!form.is_active"
                @input="onRatioInput"
            >

            <small class="compact-help-text">
              Valoarea reprezintă raportul lățime:înălțime. Exemplu: 2 = 2:1
            </small>
          </div>

          <div class="form-group col-12 col-md-4 compact-form-group">
            <label class="compact-label compact-label--ghost">
              &nbsp;
            </label>

            <div class="compact-check-box">
              <input
                  id="widget-carousel-auto-scroll"
                  type="checkbox"
                  class="compact-check-box__input"
                  :checked="form.auto_scroll"
                  :disabled="!form.is_active"
                  @change="updateField('auto_scroll', $event.target.checked)"
              >
              <label class="compact-check-box__label" for="widget-carousel-auto-scroll">
                Auto scroll
              </label>
            </div>
          </div>

          <div class="form-group col-12 col-md-4 compact-form-group">
            <label class="compact-label compact-label--ghost">
              &nbsp;
            </label>

            <div class="compact-check-box">
              <input
                  id="widget-carousel-display-page-indicator"
                  type="checkbox"
                  class="compact-check-box__input"
                  :checked="form.display_page_indicator"
                  :disabled="!form.is_active"
                  @change="updateField('display_page_indicator', $event.target.checked)"
              >
              <label class="compact-check-box__label" for="widget-carousel-display-page-indicator">
                Afișează page indicator
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const STORE_NAMESPACE = "widgetCarousel";

export default {
  name: "WidgetCarouselGeneralSettings",

  props: {
    form: {
      type: Object,
      required: true
    }
  },

  computed: {
    hasProducts() {
      return Array.isArray(this.form.products) && this.form.products.length > 0;
    },
    priorityOptions() {
      return Array.from({ length: 25 }, (_, index) => 80 + index * 5);
    }
  },

  methods: {
    updateField(field, value) {
      if (typeof field === "object") {
        this.$store.dispatch(`${STORE_NAMESPACE}/updateField`, field);
        return;
      }

      this.$store.dispatch(`${STORE_NAMESPACE}/updateField`, {
        field,
        value
      });
    },

    onRatioInput(event) {
      if (!this.form.is_active) {
        return;
      }

      let value = Number(event.target.value);

      if (Number.isNaN(value)) {
        value = 2;
      }

      if (value < 2) {
        value = 2;
      }

      if (value > 10) {
        value = 10;
      }

      this.updateField("ratio", value);
    },

    onWidgetIdInput(event) {
      const slug = this.slugifyWithUnderscore(event.target.value);
      this.updateField("widget_id", slug);
    },

    normalizeDateTimeLocal(value) {
      if (!value) {
        return "";
      }

      return String(value).replace(" ", "T").slice(0, 16);
    },

    slugifyWithUnderscore(value) {
      return String(value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "");
    }
  }
};
</script>

<style scoped>
.compact-form-card__header {
  padding: 10px 14px;
  font-size: 14px;
}

.compact-form-card__body {
  padding: 14px;
}

.compact-help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
}

.compact-form-row--top {
  align-items: flex-start;
}

.compact-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.widget-inline-field {
  display: flex;
  align-items: center;
  min-height: 38px;
}

.widget-inline-field--checkbox {
  padding: 0 2px;
}

.compact-check-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.compact-help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.35;
  color: #6c757d;
}
</style>